<template>
  <div>
    <el-dialog
      :visible.sync="showDialog"
      :before-close="handleClose"
      :width="modalWidth"
      top="15vh"
    >
      <div class="font-normal mb-4 mx-4">
        <div>
          <div
            class="font-bold flex justify-center -mt-4 mb-3 text-root-2 text-2xl"
          >
            Interest Calculator
          </div>
          <el-form
            :model="interestCalculatorForm"
            ref="interestCalculatorForm"
            :rules="rules"
            size="normal"
          >
            <el-form-item label="Select Currency" prop="network">
              <el-select
                class="w-full"
                v-model="interestCalculatorForm.currency"
                placeholder="Select Currency"
              >
                <el-option
                  v-for="item in currency"
                  :key="item.value"
                  :label="item.label"
                  :value="item.value"
                >
                </el-option>
              </el-select>
            </el-form-item>
            <el-form-item class="w-full" label="Enter an Amount" prop="amount">
              <el-input
                prop="amount"
                class="text-xl"
                v-model="interestCalculatorForm.amount"
                name="amount"
                placeholder="$0.00"
              >
              </el-input>
            </el-form-item>
            <div class="w-full mt-3 justify-end flex">
              <div
                @click="getTotalBalance"
                class="text-primary text-xs font-normal bg-orange-3 cursor-pointer rounded-lg mb-2 py-1 px-3"
              >
                Use Balance
              </div>
            </div>
          </el-form>
        </div>
        <div>
          <div
            class="text-sm text-root-4 pt-4 font-normal border-t border-t-text-root-3 mt-6"
          >
            Projected {{ active }}
            {{ active === 1 ? 'year' : 'years' }} interest amount
          </div>
          <div class="p-1 flex items-center text-primary">
            <interest-up class="mr-2" /> {{ apyValue }}% APY
          </div>
        </div>
        <div class="text-4xl text-root-1 flex pt-2 justify-start font-semibold">
          {{ parseMoney(getInterestAmount) }}
        </div>
        <span
          class="font-normal text-xs mb-1 mt-5 flex justify-between items-center"
          ><span> Duration</span>
        </span>
        <el-progress
          class="mb-4"
          :format="format"
          :percentage="durationPercentage"
        ></el-progress>
        <div
          class="flex text-xs font-normal items-center text-root-4 justify-between"
        >
          <div
            @click="active = 1"
            :class="
              active === 1 ? 'bg-primary font-bold text-white' : 'bg-root-3'
            "
            class="px-4 cursor-pointer py-1.5 rounded-full"
          >
            1 year
          </div>
          <div
            @click="active = 2"
            :class="
              active === 2 ? 'bg-primary font-bold text-white' : 'bg-root-3'
            "
            class="px-4 cursor-pointer py-1.5 rounded-full"
          >
            2 years
          </div>
          <div
            @click="active = 3"
            :class="
              active === 3 ? 'bg-primary font-bold text-white' : 'bg-root-3'
            "
            class="px-4 cursor-pointer py-1.5 rounded-full"
          >
            3 years
          </div>
          <div
            @click="active = 4"
            :class="
              active === 4 ? 'bg-primary font-bold text-white' : 'bg-root-3'
            "
            class="px-4 cursor-pointer py-1.5 rounded-full"
          >
            4 years
          </div>
        </div>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import {
  FormatAmount,
  ParseMoney,
  expectedInterestYears
} from '@/utils/helpers'
import breakpoints from '@/utils/breakpoints.js'

import { CURRENCY } from '@/utils/constants'
import getBalance from '@/mixins/getBalance.js'

export default {
  mixins: [getBalance],
  data () {
    return {
      active: 1,
      activeName: '',
      currency: CURRENCY,
      amount: 0,
      interestCalculatorForm: {
        amount: '',
        currency: 'USD'
      },
      rules: {
        amount: [
          {
            required: true,
            message: 'Please input a valid amount',
            trigger: ['blur', 'change']
          }
        ]
      }
    }
  },
  props: {
    showDialog: {
      type: Boolean,
      default: false
    }
  },
  components: {
    InterestUp: () => import('@/assets/icons/InterestUp.vue')
  },
  computed: {
    getInterestAmount () {
      const amount = expectedInterestYears(
        this.apyValue,
        this.active,
        FormatAmount(this.interestCalculatorForm.amount)
      )
      return amount || 0
    },
    durationPercentage () {
      return this.active * 25
    },
    modalWidth () {
      return breakpoints.lgAndUp ? '35%' : breakpoints.mdAndUp ? '70%' : '90%'
    }
  },
  watch: {
    'interestCalculatorForm.amount': function (newValue) {
      this.amount = newValue
      const result = newValue
        .toString()
        .split('.')[0]
        .replace(/\D/g, '')
        .replace(/\B(?=(\d{3})+(?!\d))/g, ',')
        .replace(/^0+/g, '')
      this.$nextTick(() => {
        this.interestCalculatorForm.amount = newValue.toString().split('.')[1]
          ? ` $${result}.${newValue.toString().split('.')[1]}`
          : `$${result}`
      })
    },
    deep: true
  },
  methods: {
    format () {
      return ''
    },
    parseMoney (val) {
      return ParseMoney(val)
    },
    handleClose () {
      this.$emit('closeDialog')
    },
    validateAmount (rule, value, callback) {
      if (FormatAmount(value) > this.totalBalance) {
        callback(new Error("That's more than your balance"))
      } else if (!FormatAmount(value) || FormatAmount(value) < 1) {
        callback(new Error('Provide an amount'))
      } else {
        callback()
      }
    },
    formatAmount (val) {
      return FormatAmount(val)
    },
    getTotalBalance () {
      this.interestCalculatorForm.amount = ParseMoney(this.totalBalance)
    }
  }
}
</script>

<style scoped>
.text-root-1 {
  color: var(--text-1);
}
.text-root-2 {
  color: var(--text-2);
}
.text-root-4 {
  color: var(--text-4);
}
.border-text-root-3 {
  border: 1px var(--text-3) solid;
}
.border-t-text-root-3 {
  border-top: 1px var(--text-3) solid;
}
>>> .el-dialog__headerbtn .el-dialog__close {
  margin-right: 12px;
  margin-top: 10px;
}
>>> .el-dialog {
  border-radius: 30px;
  padding: 5px;
  background-color: var(--background);
}
>>> .el-input__inner {
  background-color: var(--background);
  color: var(--text-1);
  height: 50px;
  border: 1px solid var(--text-5);
}
>>> .el-input__inner {
  border-radius: 8px;
  border-color: var(--text-5);
  height: 48px;
}
>>> .el-form-item {
  margin-bottom: 10px;
}
>>> .el-form-item__label {
  margin-bottom: -8px;
}

>>> .el-select .el-input__inner:focus {
  border-radius: 8px;
  border-color: #c7c8d2;
}
>>> .el-form--label-top .el-form-item__label {
  padding: 0;
}

>>> .el-form-item__label {
  margin-bottom: -8px;
}
>>> .el-progress-bar__inner {
  background: linear-gradient(270deg, #fc4a1a 0%, #f7b733 100%);
}
>>> .el-input__inner:focus {
  border-color: #818799;
}
>>> .el-input__inner {
  background-color: var(--background);
  color: var(--text-1) !important;
  height: 50px;
  border: 1px solid var(--text-3) !important;
}
>>> .el-progress-bar {
  width: 110%;
}
</style>
